import React from "react";
import { useVisibility } from "../../contexts/VisibilityContext";
import NoAccess from "../common/NoAccess";

/**
 * Route/page guard for the profession-based Visibility Settings.
 *
 * Renders {children} only if every required key is enabled for the current user.
 * Managers (ADMIN / BERUFSBILDNER) receive fullAccess and always pass, and keys
 * that are not part of the user's own access map default to allowed — so a single
 * guard listing both a learner key and the equivalent practice-instructor key only
 * blocks the role the disabled key actually applies to.
 *
 * The backend still enforces the same rules and returns 403; this guard just
 * prevents rendering protected pages after a direct URL navigation.
 *
 * Two modes:
 *   keys   — ALL listed keys must be enabled (default; safe for multi-role routes).
 *   anyKey — access is granted if AT LEAST ONE listed key is enabled. Use this for
 *            multi-purpose pages, e.g. the trainer reviews page that a practice
 *            instructor may reach either to view reviews OR to create educational
 *            reports (Bildungsberichte).
 *
 * Usage:
 *   <RequireVisibilityKey anyKey={["VIEW_PAST_SEMESTER_REVIEWS", "CREATE_EDUCATIONAL_REPORTS"]}>
 *     <Page />
 *   </RequireVisibilityKey>
 */
function RequireVisibilityKey({ keys, anyKey, children }) {
  const { canAccess, loaded } = useVisibility();

  // Avoid flashing the NoAccess page before the /me map has loaded.
  if (!loaded) {
    return (
      <div className="min-h-[40vh] grid place-items-center text-ink-muted">
        <i className="fas fa-spinner fa-spin text-xl" />
      </div>
    );
  }

  let allowed;
  if (anyKey !== undefined) {
    const anyRequired = Array.isArray(anyKey) ? anyKey : [anyKey];
    allowed = anyRequired.some((key) => canAccess(key));
  } else {
    const required = Array.isArray(keys) ? keys : [keys];
    allowed = required.every((key) => canAccess(key));
  }
  if (!allowed) {
    return (
      <NoAccess
        title="No Access"
        message="You do not have permission to view this page."
      />
    );
  }

  return children;
}

export default RequireVisibilityKey;
